import React from "react";
import { Dialog, DialogContent, Button as MuiButton, AppBar, Toolbar, Typography, IconButton, Box, useTheme, styled, Divider, Stack } from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { formatDateTime } from "../../../helpers/utils";
import { kontram } from "../../../../theme/muiTheme";

const QAL3PrintVersion = ({
  isOpen,
  onClose,
  values,
  systemName,
  loading,
  t
}) => {

  const handlePrint = () => {
    window.print();
  };

  const theme = useTheme();

  const StyledTypography = styled(Typography)({
    color: 'black'
  });

  const responseCheck = (responseStatus) => {
    return responseStatus == 1 ? "Under Limit" : "Over Limit";
  };

  const driftCheck = (status) => {
    if (status == 1) {
      return "Checked";
    }
    if (status == 2) {
      return "Calibrate";
    }
    if (status == -1) {
      return "Failure";
    }
    return "Not Tested";
  };

  const energyValueStringSlicer = (string) => {
    let slicedString = "";
    let stringLength = string.length;

    if (stringLength === 8) {
      slicedString = string.slice(0, 3) + " " + string.slice(3, 5) + " " + string.slice(5, 8);
    } else if (stringLength === 9) {
      slicedString = string.slice(0, 3) + " " + string.slice(3, 6) + " " + string.slice(6, 9);
    } else {
      slicedString = string;
    }
    return slicedString;
  };

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      sx={{ backgroundColor: 'white' }}
    >
      <AppBar sx={{
        '@media print': {
          display: 'none',
        },
        position: 'relative',
        backgroundColor: kontram.primary
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            QAL3 Report - Print View
          </Typography>
          <MuiButton
            color="inherit"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
          >
            Print
          </MuiButton>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ padding: 3, minHeight: '100vh', backgroundColor: 'white' }}>
        <StyledTypography
          variant="h5" sx={{ textAlign: 'center' }}
        >
          QAL3 Reports
        </StyledTypography>
        <StyledTypography
          variant="h6" sx={{ mb: 2, textAlign: 'center' }}
        >
          {systemName}
        </StyledTypography>

        {/* render reports */}
        {!loading &&
          values &&
          values.length > 0 &&
          values.map((val, idx) => (
            <Box
              key={`print-qal3-${idx}`}
              sx={{
                border: `2px solid ${theme.palette.grey[700]}`,
                borderRadius: 1,
                padding: 2,
                marginBottom: 3,
                '@media print': {
                  pageBreakAfter: 'always',
                  '&:last-child': {
                    pageBreakAfter: 'avoid'
                  }
                }
              }}
            >
              {/* Report Header */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Date:
                  </StyledTypography>
                  <StyledTypography>
                    {formatDateTime(val.ReportTimeStamp)}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Run Type:
                  </StyledTypography>
                  <StyledTypography>
                    {val.RunType}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Duration:
                  </StyledTypography>
                  <StyledTypography>
                    {Math.floor(val.Duration / 60) + " min " + Math.floor(val.Duration % 60) + "s"}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Result:
                  </StyledTypography>
                  <Typography
                    backgroundColor={
                      val.State === "QAL3 Failed"
                        ? theme.palette.error.dark
                        : theme.palette.success.dark
                    }
                    sx={{ px: 1, borderRadius: 1 }}
                  >
                    {val.State}
                  </Typography>
                </Stack>
              </Box>

              {/* Test Summary */}
              <Divider color={theme.palette.common.black} sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Drift Tests:
                  </StyledTypography>
                  <StyledTypography>
                    {val.DriftTests}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Calibrations:
                  </StyledTypography>
                  <StyledTypography>
                    {val.Calibrations}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Response Times:
                  </StyledTypography>
                  <StyledTypography>
                    {val.ResponseTimes}
                  </StyledTypography>
                </Stack>
              </Box>

              <Divider color={theme.palette.common.black} sx={{ my: 2 }} />

              {/* Analysis Results Header */}
              <StyledTypography sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.1rem' }}>
                Analysis Results:
              </StyledTypography>

              {/* Analysis Results Table */}
              <Box sx={{ display: 'flex', flexDirection: 'column', border: `1px solid ${theme.palette.grey[400]}` }}>
                {/* Table Header */}
                <Box sx={{
                  display: 'flex',
                  backgroundColor: theme.palette.grey[200],
                  fontWeight: 'bold',
                  borderBottom: `1px solid ${theme.palette.grey[800]}`
                }}>
                  <Box sx={{ flex: 1, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Gas</StyledTypography>
                  </Box>
                  <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Zero Drift</StyledTypography>
                  </Box>
                  <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Span Drift</StyledTypography>
                  </Box>
                  <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Zero Response</StyledTypography>
                  </Box>
                  <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Span Response</StyledTypography>
                  </Box>
                  <Box sx={{ flex: 1.2, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Zero Result</StyledTypography>
                  </Box>
                  <Box sx={{ flex: 1.2, p: 1 }}>
                    <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>Span Result</StyledTypography>
                  </Box>
                </Box>

                {/* Table Rows */}
                {val.ComponentResults && Object.entries(val.ComponentResults).map(([gas, gasData], gasIdx) => (
                  <Box key={`print-gas-${idx}-${gasIdx}`} sx={{
                    display: 'flex',
                    '&:nth-of-type(even)': { backgroundColor: theme.palette.grey[300] }
                  }}>
                    <Box sx={{ flex: 1, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                      <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>{gas}</StyledTypography>
                    </Box>
                    <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                      <StyledTypography variant="body2">
                        {Number(gasData.ZeroDrift).toFixed(1)} {gasData.Unit}
                      </StyledTypography>
                      <StyledTypography variant="caption" display="block" color="textSecondary">
                        ({gasData.ZeroCalLimit}/{gasData.ZeroFailLimit} {gasData.Unit})
                      </StyledTypography>
                    </Box>
                    <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                      <StyledTypography variant="body2">
                        {Number(gasData.SpanDrift).toFixed(1)} %
                      </StyledTypography>
                      <StyledTypography variant="caption" display="block" color="textSecondary">
                        ({gasData.SpanCalLimit}/{gasData.SpanFailLimit} %)
                      </StyledTypography>
                    </Box>
                    <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                      <StyledTypography variant="body2">
                        {Math.round(gasData.ZeroResponseTime)} s
                      </StyledTypography>
                      <StyledTypography variant="caption" display="block" color="textSecondary">
                        ({gasData.ZeroResponseTimeLimit} s)
                      </StyledTypography>
                    </Box>
                    <Box sx={{ flex: 1.5, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                      <StyledTypography variant="body2">
                        {Math.round(gasData.SpanResponseTime)} s
                      </StyledTypography>
                      <StyledTypography variant="caption" display="block" color="textSecondary">
                        ({gasData.SpanResponseTimeLimit} s)
                      </StyledTypography>
                    </Box>
                    <Box sx={{ flex: 1.2, p: 1, borderRight: `1px solid ${theme.palette.grey[400]}` }}>
                      <Typography
                        variant="body2"
                        backgroundColor={
                          driftCheck(gasData.SpanDriftStatus) === "Failure"
                            ? theme.palette.error.main
                            : driftCheck(gasData.SpanDriftStatus) === "Calibrate"
                              ? theme.palette.warning.main
                              : driftCheck(gasData.SpanDriftStatus) === "Success"
                                ? theme.palette.success.main
                                : theme.palette.grey[800]
                        }
                        sx={{ px: 0.5, borderRadius: 0.5 }}
                      >
                        {driftCheck(gasData.ZeroDriftStatus)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1.2, p: 1 }}>
                      <Typography
                        variant="body2"
                        backgroundColor={
                          driftCheck(gasData.SpanDriftStatus) === "Failure"
                            ? theme.palette.error.main
                            : driftCheck(gasData.SpanDriftStatus) === "Calibrate"
                              ? theme.palette.warning.main
                              : driftCheck(gasData.SpanDriftStatus) === "Success"
                                ? theme.palette.success.main
                                : theme.palette.grey[800]
                        }
                        sx={{ px: 0.5, borderRadius: 0.5 }}
                      >
                        {driftCheck(gasData.SpanDriftStatus)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Extra info, about alarms and so on*/}
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {val.ActiveAlarms.length > 0 && (
                  <Stack direction="column" sx={{ flex: 1 }}>
                    <StyledTypography sx={{ fontWeight: 'bold', mb: 1 }}>
                      ACTIVE ALARMS:
                    </StyledTypography>
                    {val.ActiveAlarms.map((alarm, alarmIdx) => (
                      <StyledTypography key={`print-alarm-${idx}-${alarmIdx}`}>
                        - {alarm}
                      </StyledTypography>
                    ))}
                  </Stack>
                )}
                {val.Diagnostics && (
                  <Stack sx={{ flex: 1 }}>

                    <StyledTypography sx={{ fontWeight: 'bold', mb: 1 }}>
                      DIAGNOSTICS:
                    </StyledTypography>
                    {Object.entries(val.Diagnostics).map(([key, diagnostic], diagIdx) => (
                      <Stack direction="row" key={`print-diagnostic-${idx}-${diagIdx}`}  >
                        <StyledTypography sx={{ minWidth: '265px' }}>
                          - {key}:
                        </StyledTypography>
                        <StyledTypography >
                          {diagnostic.DiagnosticsValue.toFixed(3)} {diagnostic.DiagnosticsUnit}
                        </StyledTypography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
              {/* end of extra info */}


              {/* Energy Levels Section */}
              {/* {val.EnergyLevels && (
                <>
                  <Divider color={theme.palette.common.black} sx={{ my: 2 }} />
                  <StyledTypography sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.1rem' }}>
                    Energy Levels:
                  </StyledTypography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(val.EnergyLevels).map(([key, value], energyIdx) => (
                      <Box
                        key={`print-energy-${idx}-${energyIdx}`}
                        sx={{
                          border: `1px solid ${theme.palette.grey[500]}`,
                          padding: 1,
                          minWidth: "120px",
                          borderRadius: 1
                        }}
                      >
                        <StyledTypography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {energyValueStringSlicer(key)}
                        </StyledTypography>
                        <StyledTypography variant="body2">
                          {value.toFixed(2)} %
                        </StyledTypography>
                      </Box>
                    ))}
                  </Box>
                </>
              )} */}
            </Box>
          ))}
      </DialogContent>
    </Dialog>
  );
};

export default QAL3PrintVersion;